import { http } from './http';
import { ROUTES } from './config';
import { tokenStore } from './tokenStore';
import type { LoginRequest, RegisterRequest, RoleInfo, TokenPair } from '../types/domain';

export interface MeResponse {
  email: string;
  name: string;
  authProvider: 'password' | 'google' | 'guest';
  emailVerified: boolean;
}

/**
 * IdentityService client. login/refresh return a TokenPair; the tokenStore is
 * updated here so the http client can use it on the next authenticated call.
 * The current user for role/routing checks is decoded from the JWT (see decodeUser in the
 * auth store) — `me()` below is a separate, small real endpoint (GET /api/v1/auth/me) used only
 * for identity fields the JWT doesn't carry, like AuthProvider for a "Connected accounts" UI.
 */
export const authApi = {
  async login(body: LoginRequest): Promise<TokenPair> {
    const pair = await http.post<TokenPair>(ROUTES.auth.login, body, { anonymous: true });
    tokenStore.set(pair);
    return pair;
  },

  /** Exchanges a real Google ID token (obtained client-side via Firebase Auth) for a
   * ServiceVault token pair — POST /api/v1/auth/google, auto-provisions a customer account by
   * verified email if this is a first-time sign-in. 501s if the backend has no
   * Firebase__ProjectId / Google__ClientId configured. */
  async google(idToken: string): Promise<TokenPair> {
    const pair = await http.post<TokenPair>(ROUTES.auth.google, { idToken }, { anonymous: true });
    tokenStore.set(pair);
    return pair;
  },

  /** Guest checkout — POST /api/v1/auth/guest creates a passwordless customer account for this
   * email and returns a normal token pair. 409 `email-taken` if the address already has any
   * account (the caller should send the user to sign in / reset their password instead). */
  async guest(email: string, name: string): Promise<TokenPair> {
    const pair = await http.post<TokenPair>(ROUTES.auth.guest, { email, name }, { anonymous: true });
    tokenStore.set(pair);
    return pair;
  },

  me(): Promise<MeResponse> {
    return http.get(ROUTES.auth.me);
  },

  /** What the signed-in user may do, per (module, action) of the permission matrix — same verdict the gateway applies. */
  myPermissions(): Promise<{ module: string; action: string; allowed: boolean }[]> {
    return http.get('/api/v1/auth/my-permissions');
  },

  async register(body: RegisterRequest): Promise<{ userId: string }> {
    return http.post(ROUTES.auth.register, body, { anonymous: true });
  },

  async refresh(refreshToken: string): Promise<TokenPair> {
    const pair = await http.post<TokenPair>(ROUTES.auth.refresh, { refreshToken }, { anonymous: true });
    tokenStore.set(pair);
    return pair;
  },

  /** Local sign-out — the backend revokes refresh tokens on password reset / role change. */
  logout(): void {
    tokenStore.clear();
  },

  requestPasswordReset(email: string): Promise<void> {
    return http.post(ROUTES.auth.forgotPassword, { email }, { anonymous: true });
  },

  resetPassword(token: string, newPassword: string): Promise<void> {
    return http.post(ROUTES.auth.resetPassword, { token, newPassword }, { anonymous: true });
  },

  verifyEmail(token: string): Promise<void> {
    return http.post(ROUTES.auth.verifyEmail, { token }, { anonymous: true });
  },

  listRoles(): Promise<RoleInfo[]> {
    return http.get(ROUTES.auth.roles);
  },

  /** Staff seat count (admin/manager/worker) for the caller's own tenant — powers the
   * subscription usage bar. Manager+; deliberately separate from listUsers (admin-only PII). */
  async getUserCount(): Promise<number> {
    const { count } = await http.get<{ count: number }>(ROUTES.auth.userCount);
    return count;
  },

  /** Platform admin only — sends (or re-sends, calling again for the same tenant/email is safe:
   * StoreInviteFlows.InviteStoreOwnerHandler has no pending-invite uniqueness check, only the
   * final Users-table email check, so an unused prior invite is simply superseded) a
   * store-owner invite for a tenant already created via tenantsApi.create. */
  inviteStoreOwner(tenantId: string, email: string, name: string): Promise<void> {
    return http.post(ROUTES.auth.inviteStoreOwner, { tenantId, email, name });
  },

  /** Anonymous — the invite token itself is the credential. Returns a real token pair, same as login. */
  async acceptStoreInvite(token: string, password: string): Promise<TokenPair> {
    const pair = await http.post<TokenPair>(ROUTES.auth.acceptStoreInvite, { token, password }, { anonymous: true });
    tokenStore.set(pair);
    return pair;
  },
};
